import React, { useCallback, useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { FieldErrorMessage } from "../ui/Text";
import { Validation } from "@/types/form";
import RenderLabel from "@/components/Form/Renderer/RenderLabel";
import { getNestedValue } from "@/utils/helper/form";

interface SocialField {
  id: string;
  label?: string;
}

interface SocialInputProps {
  fields: SocialField[];
  field: string;
  validation?: Validation;
  defaultValue?: string;
  disabled?: boolean;
}

const SocialInput: React.FC<SocialInputProps> = ({ disabled = false, field, fields, defaultValue, validation }) => {
  const {
    register,
    setValue,
    formState: { errors },
  } = useFormContext();

  const getRegisterOptions = useCallback(() => {
    const registerOptions: any = {};
    const minLength = validation?.minLength;
    const maxLength = validation?.maxLength || 200;
    // If field is disabled
    if (disabled) {
      return {};
    }
    if (validation?.required) {
      registerOptions.required = "This field is required.";
    }
    if (minLength && maxLength && minLength === maxLength) {
      const exactLengthMessage = `Exactly ${minLength} characters are required.`;
      registerOptions.minLength = { value: minLength, message: exactLengthMessage };
      registerOptions.maxLength = { value: maxLength, message: exactLengthMessage };
    } else {
      if (minLength) {
        registerOptions.minLength = {
          value: minLength,
          message: `The minimum character allowed is ${minLength}.`,
        };
      }
      if (maxLength) {
        registerOptions.maxLength = {
          value: maxLength,
          message: `The maximum character allowed is ${maxLength}.`,
        };
      }
    }
    if (validation?.pattern) {
      registerOptions.validate = {
        pattern: (value: string) => {
          const patternValue = validation?.pattern?.value;
          if (!patternValue) throw Error("Invalid Pattern");
          const regex = new RegExp(patternValue);

          if (regex.test(value)) {
            return true;
          } else {
            if (!validation?.required && !value) return true;
            return validation.pattern?.message || "Invalid format";
          }
        },
      };
    }
    return registerOptions;
  }, []);

  useEffect(() => {
    if (defaultValue) {
      setValue(field, defaultValue, { shouldValidate: false, shouldDirty: false });
    }
  }, [defaultValue]);

  return (
    <div className="flex flex-col gap-1">
      {fields?.map((socialField) => {
        const fieldName = `${field}.${socialField.id}`;
        const errorMessage: any = getNestedValue(errors, fieldName)?.message;
        return (
          <div key={socialField.id} className="py-1 ">
            {socialField?.label && <RenderLabel label={socialField.label} className="pb-1" />}
            <input
              type="text"
              id={socialField.id + "_link"}
              className={`w-full px-3 py-7 text-text-default text-13 rounded-lg outline-none border border-fieldBorder hover:border-gray-400 focus:border-text-primary-500  ${
                errorMessage ? "border-text-error" : null
              }`}
              {...register(fieldName, getRegisterOptions())}
              disabled={disabled}
              autoComplete="off"
            />
            {errorMessage && <FieldErrorMessage text={errorMessage} />}
          </div>
        );
      })}
    </div>
  );
};

export default SocialInput;
